import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Star, CheckCircle, Clock, Users, IndianRupee, TicketCheck, Briefcase, Save, FileText, Loader2, BarChart3, CalendarDays, UserPlus, Mail, Send, Search, Eye, Sparkles, Pencil, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { toLocalISODate } from '../lib/formatUtils';
import { formatDate } from '../lib/formatUtils';

const RATING_PARAMS = [
  { key: 'quality', label: 'Quality of Work' },
  { key: 'timeliness', label: 'Timeliness' },
  { key: 'communication', label: 'Communication' },
  { key: 'initiative', label: 'Initiative' },
];

const ACTIONS = ['Promotion', 'Increment', 'PIP', 'No Change'];

const SELF_REVIEW_QUESTIONS = [
  { key: 'key_contributions', label: 'Key Contributions' },
  { key: 'what_went_well', label: 'What Went Well' },
  { key: 'challenges', label: 'Challenges Faced' },
  { key: 'learning_growth', label: 'Learning & Growth' },
  { key: 'next_year_goals', label: 'Goals for Next Year' },
  { key: 'support_needed', label: 'Support Needed' },
  { key: 'compensation_expectation', label: 'Compensation Expectation' },
  { key: 'feedback_for_company', label: 'Feedback for the Company' },
];

const currentYear = new Date().getFullYear();
const fyOptions = [];
for (let y = currentYear; y >= currentYear - 5; y--) {
  fyOptions.push(`FY ${y}-${String(y + 1).slice(2)}`);
}

const RatingStars = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(n)} className="focus:outline-none" data-testid={`star-${n}`}>
        <Star className={`h-5 w-5 transition-colors ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
      </button>
    ))}
  </div>
);

// Helper: months since joining
const monthsSince = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  return (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
};

// =====================================================
// CYCLE MANAGEMENT (Admin)
// =====================================================
const CycleManagement = ({ employees, onViewSelfReview }) => {
  const [cycles, setCycles] = useState([]);
  const [activeCycle, setActiveCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  const loadCycles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/appraisals/cycles');
      if (r.data.success) setCycles(r.data.data);
    } catch {
      toast.error('Failed to load appraisal cycles');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCycleDetails = async (cycleId) => {
    try {
      const r = await api.get(`/appraisals/cycles/${cycleId}`);
      if (r.data.success) setActiveCycle(r.data.data);
    } catch {
      toast.error('Failed to load cycle details');
    }
  };

  useEffect(() => { loadCycles(); }, [loadCycles]);

  const onCycleCreated = (cycle) => {
    setShowCreateModal(false);
    loadCycles();
    loadCycleDetails(cycle.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Appraisal Cycles</h2>
          <p className="text-sm text-muted-foreground">Initiate cycles, track self-review submissions, and review responses.</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="open-create-cycle">
          <UserPlus className="h-4 w-4 mr-1.5" /> Start New Cycle
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : cycles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarDays className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-semibold">No appraisal cycles yet</p>
            <p className="text-xs text-muted-foreground mt-1">Click "Start New Cycle" to initiate the first one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {cycles.map(c => (
            <Card key={c.id}
              className={`cursor-pointer transition-colors ${activeCycle?.id === c.id ? 'border-primary bg-primary/5' : 'hover:border-primary/30'}`}
              onClick={() => loadCycleDetails(c.id)}
              data-testid={`cycle-card-${c.fy.replace(/\s/g, '-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold">{c.fy}</p>
                  <Badge variant="outline" className="text-xs">{c.selected_employees?.length || 0} emp.</Badge>
                </div>
                <p className="text-xs text-muted-foreground"><CalendarDays className="inline h-3 w-3 mr-1" />Deadline: {formatDate(c.deadline)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeCycle && (
        <Card data-testid="active-cycle-detail">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base">Cycle: {activeCycle.fy}</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Deadline: {formatDate(activeCycle.deadline)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-primary/10 text-primary">
                  {activeCycle.employees?.filter(e => e.review_status === 'submitted').length || 0} / {activeCycle.employees?.length || 0} Submitted
                </Badge>
                <Button variant="outline" size="sm" onClick={() => setShowManageModal(true)} data-testid="manage-cycle-employees">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Manage Employees & Deadline
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted At</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(activeCycle.employees || []).map(emp => (
                  <TableRow key={emp.id} data-testid={`cycle-emp-${emp.id}`}>
                    <TableCell className="font-medium">{emp.first_name} {emp.last_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{emp.department || '-'}</TableCell>
                    <TableCell>
                      {emp.review_status === 'submitted' ? <Badge className="bg-emerald-500/10 text-emerald-500">Submitted</Badge>
                        : emp.review_status === 'draft' ? <Badge className="bg-amber-500/10 text-amber-500">Draft</Badge>
                        : <Badge className="bg-muted text-muted-foreground">Pending</Badge>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{emp.submitted_at ? formatDate(emp.submitted_at) : '-'}</TableCell>
                    <TableCell className="text-right">
                      {emp.review_status === 'submitted' && (
                        <Button variant="ghost" size="sm" onClick={() => onViewSelfReview(emp.id, activeCycle.fy)} data-testid={`view-review-${emp.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {showCreateModal && (
        <CreateCycleDialog
          employees={employees}
          existingCycles={cycles}
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={onCycleCreated}
        />
      )}

      {showManageModal && activeCycle && (
        <ManageCycleDialog
          cycle={activeCycle}
          employees={employees}
          open={showManageModal}
          onClose={() => setShowManageModal(false)}
          onUpdated={() => {
            setShowManageModal(false);
            loadCycles();
            loadCycleDetails(activeCycle.id);
          }}
        />
      )}
    </div>
  );
};

// =====================================================
// CREATE CYCLE DIALOG
// =====================================================
const CreateCycleDialog = ({ employees, existingCycles, open, onClose, onCreated }) => {
  const [fy, setFy] = useState(fyOptions[0]);
  const [deadline, setDeadline] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return toLocalISODate(d);
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const existingFys = new Set(existingCycles.map(c => c.fy));
  const availableFys = fyOptions.filter(o => !existingFys.has(o));

  useEffect(() => {
    if (availableFys.length > 0 && !availableFys.includes(fy)) {
      setFy(availableFys[0]);
    }
  }, [availableFys, fy]);

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    return !q || `${e.first_name} ${e.last_name} ${e.department || ''} ${e.designation || ''} ${e.employee_id || ''}`.toLowerCase().includes(q);
  });

  const toggleEmp = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedIds.length === filtered.length) setSelectedIds([]);
    else setSelectedIds(filtered.map(e => e.id));
  };

  const handleCreate = async () => {
    if (!fy || !deadline || selectedIds.length === 0) {
      toast.error('Select FY, deadline, and at least one employee.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/appraisals/cycles', { fy, deadline, employee_ids: selectedIds });
      if (r.data.success) {
        toast.success(r.data.message || 'Cycle created');
        onCreated(r.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create cycle');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Start New Appraisal Cycle</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Financial Year</Label>
            <Select value={fy} onValueChange={setFy}>
              <SelectTrigger data-testid="cycle-fy"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableFys.length === 0 ? (
                  <SelectItem value="__none__" disabled>All FYs already have cycles</SelectItem>
                ) : availableFys.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Submission Deadline</Label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} min={toLocalISODate()} data-testid="cycle-deadline" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Select Employees</Label>
            <Badge variant="outline" data-testid="selected-count">{selectedIds.length} selected</Badge>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by name, department..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="emp-search" />
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-muted/30 p-2 flex items-center gap-2 border-b text-sm">
              <Checkbox checked={filtered.length > 0 && selectedIds.length === filtered.length} onCheckedChange={toggleAll} data-testid="select-all" />
              <span className="font-medium">Select All ({filtered.length})</span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.map(emp => {
                const months = monthsSince(emp.joining_date);
                const isNew = months !== null && months < 6;
                return (
                  <div key={emp.id}
                    className={`flex items-center gap-3 p-2.5 border-b hover:bg-muted/30 ${selectedIds.includes(emp.id) ? 'bg-primary/5' : ''}`}
                    data-testid={`emp-row-${emp.id}`}>
                    <Checkbox checked={selectedIds.includes(emp.id)} onCheckedChange={() => toggleEmp(emp.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{emp.first_name} {emp.last_name}</p>
                        {isNew && <Badge className="bg-amber-500/10 text-amber-600 border border-amber-500/30 text-[10px] py-0 px-1.5">New hire ({months}mo)</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{emp.designation || '-'} · {emp.department || '-'} · {emp.official_email || emp.email || '-'}</p>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && <p className="p-4 text-sm text-center text-muted-foreground">No employees match your search.</p>}
            </div>
          </div>
        </div>

        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-3 flex items-start gap-3">
            <Mail className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium">Email Notifications</p>
              <p className="text-xs text-muted-foreground">Selected employees will receive an email with a link to fill their self-review and the deadline.</p>
            </div>
          </CardContent>
        </Card>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleCreate} disabled={submitting || selectedIds.length === 0} data-testid="create-cycle-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Initiate Cycle & Notify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =====================================================
// MANAGE CYCLE DIALOG (Add/Remove employees + edit deadline on existing cycle)
// =====================================================
const ManageCycleDialog = ({ cycle, employees, open, onClose, onUpdated }) => {
  const initialIds = cycle.selected_employees || [];
  // Map of employee_id -> review_status from the enriched cycle.employees
  const reviewStatusMap = (cycle.employees || []).reduce((acc, e) => { acc[e.id] = e.review_status; return acc; }, {});

  const [selectedIds, setSelectedIds] = useState(initialIds);
  const [deadline, setDeadline] = useState(cycle.deadline || '');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    return !q || `${e.first_name} ${e.last_name} ${e.department || ''} ${e.designation || ''} ${e.employee_id || ''}`.toLowerCase().includes(q);
  });

  const toggleEmp = (id) => {
    // Block removal if employee already submitted/drafted (data integrity)
    const status = reviewStatusMap[id];
    if (selectedIds.includes(id) && (status === 'submitted' || status === 'draft')) {
      toast.error(`Cannot remove this employee — they have already ${status === 'submitted' ? 'submitted' : 'started'} their self-review.`);
      return;
    }
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const newlyAdded = selectedIds.filter(id => !initialIds.includes(id));
  const newlyRemoved = initialIds.filter(id => !selectedIds.includes(id));
  const deadlineChanged = deadline !== cycle.deadline;
  const hasChanges = newlyAdded.length > 0 || newlyRemoved.length > 0 || deadlineChanged;

  const handleSave = async () => {
    if (!selectedIds.length) {
      toast.error('A cycle must have at least one employee.');
      return;
    }
    if (!hasChanges) {
      toast.info('No changes to save.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {};
      if (newlyAdded.length || newlyRemoved.length) payload.employee_ids = selectedIds;
      if (deadlineChanged) payload.deadline = deadline;
      const r = await api.put(`/appraisals/cycles/${cycle.id}`, payload);
      if (r.data.success) {
        toast.success(r.data.message || 'Cycle updated');
        onUpdated();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update cycle');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" /> Manage Cycle: {cycle.fy}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Submission Deadline</Label>
          <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} data-testid="manage-deadline" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Employees in this cycle</Label>
            <Badge variant="outline" data-testid="manage-selected-count">{selectedIds.length} selected</Badge>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by name, department..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="manage-emp-search" />
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-72 overflow-y-auto">
              {filtered.map(emp => {
                const months = monthsSince(emp.joining_date);
                const isNew = months !== null && months < 6;
                const status = reviewStatusMap[emp.id];
                const checked = selectedIds.includes(emp.id);
                const lockedRemove = checked && (status === 'submitted' || status === 'draft');
                return (
                  <div key={emp.id}
                    className={`flex items-center gap-3 p-2.5 border-b hover:bg-muted/30 ${checked ? 'bg-primary/5' : ''}`}
                    data-testid={`manage-emp-row-${emp.id}`}>
                    <Checkbox checked={checked} disabled={lockedRemove} onCheckedChange={() => toggleEmp(emp.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm truncate">{emp.first_name} {emp.last_name}</p>
                        {isNew && <Badge className="bg-amber-500/10 text-amber-600 border border-amber-500/30 text-[10px] py-0 px-1.5">New hire ({months}mo)</Badge>}
                        {status === 'submitted' && <Badge className="bg-emerald-500/10 text-emerald-500 text-[10px] py-0 px-1.5">Submitted (locked)</Badge>}
                        {status === 'draft' && <Badge className="bg-amber-500/10 text-amber-500 text-[10px] py-0 px-1.5">Draft started (locked)</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{emp.designation || '-'} · {emp.department || '-'} · {emp.official_email || emp.email || '-'}</p>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && <p className="p-4 text-sm text-center text-muted-foreground">No employees match your search.</p>}
            </div>
          </div>
        </div>

        {hasChanges && (
          <Card className="bg-amber-500/5 border-amber-500/20">
            <CardContent className="p-3 flex items-start gap-3 text-sm">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-1">
                {newlyAdded.length > 0 && (
                  <p><span className="font-medium text-emerald-600">+{newlyAdded.length}</span> employee(s) will be <strong>added</strong> and notified by email.</p>
                )}
                {newlyRemoved.length > 0 && (
                  <p><span className="font-medium text-red-600">−{newlyRemoved.length}</span> employee(s) will be <strong>removed</strong> from this cycle.</p>
                )}
                {deadlineChanged && (
                  <p>Deadline will change to <strong>{deadline}</strong>.</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSave} disabled={submitting || !hasChanges} data-testid="save-cycle-changes">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =====================================================
// VIEW SELF-REVIEW DIALOG (Admin)
// =====================================================
const ViewSelfReviewDialog = ({ employeeId, fy, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);

  useEffect(() => {
    if (!employeeId || !fy) return;
    setLoading(true);
    api.get(`/appraisals/self-review/${employeeId}?fy=${encodeURIComponent(fy)}`)
      .then(r => setReview(r.data.data))
      .catch(() => toast.error('Failed to load self-review'))
      .finally(() => setLoading(false));
  }, [employeeId, fy]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Self-Review — {fy}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : !review ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No self-review found.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge className={review.status === 'submitted' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}>{review.status}</Badge>
              {review.submitted_at && <span>Submitted on {formatDate(review.submitted_at)}</span>}
            </div>
            {SELF_REVIEW_QUESTIONS.map(q => (
              <Card key={q.key} data-testid={`admin-sr-${q.key}`}>
                <CardContent className="p-3">
                  <p className="text-xs font-semibold text-primary mb-1">{q.label}</p>
                  <p className="text-sm whitespace-pre-wrap">{review[q.key] || <span className="text-muted-foreground italic">No response</span>}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =====================================================
// INDIVIDUAL APPRAISAL VIEW (existing functionality)
// =====================================================
const IndividualAppraisal = ({ employees }) => {
  const [selectedEmp, setSelectedEmp] = useState('');
  const [selectedFy, setSelectedFy] = useState(fyOptions[0]);
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('performance');
  const [empSelfReview, setEmpSelfReview] = useState(null);

  const [ratings, setRatings] = useState({ quality: 0, timeliness: 0, communication: 0, initiative: 0 });
  const [remarks, setRemarks] = useState('');
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [action, setAction] = useState('No Change');
  const [currentCtc, setCurrentCtc] = useState('');
  const [proposedCtc, setProposedCtc] = useState('');
  const [incrementPct, setIncrementPct] = useState('');

  const [history, setHistory] = useState([]);

  const fetchPerformance = useCallback(async () => {
    if (!selectedEmp || !selectedFy) return;
    setLoading(true);
    setPerfData(null);
    setEmpSelfReview(null);
    try {
      const [perfRes, histRes, apprRes, srRes] = await Promise.all([
        api.get(`/appraisals/performance/${selectedEmp}?fy=${encodeURIComponent(selectedFy)}`),
        api.get(`/appraisals?employee_id=${selectedEmp}`),
        api.get(`/appraisals?employee_id=${selectedEmp}&fy=${encodeURIComponent(selectedFy)}`),
        api.get(`/appraisals/self-review/${selectedEmp}?fy=${encodeURIComponent(selectedFy)}`).catch(() => ({ data: { data: null } })),
      ]);
      if (perfRes.data.success) setPerfData(perfRes.data.data);
      if (histRes.data.success) setHistory(histRes.data.data);
      if (srRes.data?.data) setEmpSelfReview(srRes.data.data);
      if (apprRes.data.success && apprRes.data.data.length > 0) {
        const a = apprRes.data.data[0];
        setRatings(a.ratings || { quality: 0, timeliness: 0, communication: 0, initiative: 0 });
        setRemarks(a.remarks || '');
        setStrengths(a.strengths || '');
        setImprovements(a.improvements || '');
        setAction(a.recommended_action || 'No Change');
        setCurrentCtc(a.current_ctc != null ? String(a.current_ctc) : '');
        setProposedCtc(a.proposed_ctc != null ? String(a.proposed_ctc) : '');
        if (a.current_ctc && a.proposed_ctc && a.current_ctc > 0) {
          setIncrementPct(String(Math.round((a.proposed_ctc - a.current_ctc) / a.current_ctc * 10000) / 100));
        }
      } else {
        setRatings({ quality: 0, timeliness: 0, communication: 0, initiative: 0 });
        setRemarks(''); setStrengths(''); setImprovements(''); setAction('No Change');
        setProposedCtc(''); setIncrementPct('');
        const emp = employees.find(e => e.id === selectedEmp);
        setCurrentCtc(emp?.salary ? String(emp.salary) : '');
      }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [selectedEmp, selectedFy, employees]);

  useEffect(() => { if (selectedEmp && selectedFy) fetchPerformance(); }, [selectedEmp, selectedFy, fetchPerformance]);

  const handleSave = async () => {
    if (!selectedEmp || !selectedFy) return;
    setSaving(true);
    try {
      await api.post('/appraisals', {
        employee_id: selectedEmp,
        fy: selectedFy,
        ratings,
        remarks,
        strengths,
        improvements,
        recommended_action: action,
        current_ctc: currentCtc ? parseFloat(currentCtc) : null,
        proposed_ctc: proposedCtc ? parseFloat(proposedCtc) : null,
      });
      toast.success('Appraisal saved');
      fetchPerformance();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const avgRating = Object.values(ratings).filter(v => v > 0).length > 0
    ? (Object.values(ratings).reduce((a, b) => a + b, 0) / Object.values(ratings).filter(v => v > 0).length).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-6">
      {/* Selection Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 space-y-2">
              <Label>Employee</Label>
              <Select value={selectedEmp} onValueChange={setSelectedEmp}>
                <SelectTrigger data-testid="appraisal-employee"><SelectValue placeholder="Select employee..." /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.designation || e.department || e.employee_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-48 space-y-2">
              <Label>Financial Year</Label>
              <Select value={selectedFy} onValueChange={setSelectedFy}>
                <SelectTrigger data-testid="appraisal-fy"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyOptions.map(fy => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}

      {perfData && !loading && (
        <>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                  {perfData.employee.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h2 className="text-lg font-bold">{perfData.employee.name}</h2>
                  <p className="text-sm text-muted-foreground">{perfData.employee.designation} — {perfData.employee.department} — {perfData.employee.employee_id}</p>
                </div>
                <Badge className="ml-auto text-sm">{selectedFy}</Badge>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><CheckCircle className="h-5 w-5 text-blue-500" /></div>
              <div><p className="text-2xl font-bold">{perfData.tasks.total}</p><p className="text-xs text-muted-foreground">Tasks</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-emerald-500" /></div>
              <div><p className="text-2xl font-bold">{Number(perfData.tasks.completed_value).toLocaleString('en-IN')}</p><p className="text-xs text-muted-foreground">Completed Value</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><TicketCheck className="h-5 w-5 text-amber-500" /></div>
              <div><p className="text-2xl font-bold">{perfData.tickets.resolved}/{perfData.tickets.total}</p><p className="text-xs text-muted-foreground">Tickets Resolved</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center"><Briefcase className="h-5 w-5 text-purple-500" /></div>
              <div><p className="text-2xl font-bold">{perfData.leads.total}</p><p className="text-xs text-muted-foreground">Leads Managed</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-cyan-500/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-cyan-500" /></div>
              <div><p className="text-2xl font-bold">{perfData.attendance.working_days}</p><p className="text-xs text-muted-foreground">Days / {perfData.attendance.leaves_taken} Leaves</p></div>
            </CardContent></Card>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="performance" data-testid="tab-performance"><BarChart3 className="h-4 w-4 mr-1.5" />Performance</TabsTrigger>
              <TabsTrigger value="self-review" data-testid="tab-self-review"><FileText className="h-4 w-4 mr-1.5" />Self-Review {empSelfReview?.status === 'submitted' && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />}</TabsTrigger>
              <TabsTrigger value="appraisal" data-testid="tab-appraisal"><FileText className="h-4 w-4 mr-1.5" />Appraisal Form</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history"><Clock className="h-4 w-4 mr-1.5" />History</TabsTrigger>
            </TabsList>

            <TabsContent value="performance" className="space-y-4 mt-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Tasks Breakdown</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {Object.entries(perfData.tasks.by_status).map(([status, count]) => (
                      <div key={status} className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">{status.replace('_', ' ')}</Badge>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-6 text-sm">
                    <div><span className="text-muted-foreground">Total Value:</span> <span className="font-semibold text-foreground"><IndianRupee className="inline h-3.5 w-3.5" />{Number(perfData.tasks.total_value).toLocaleString('en-IN')}</span></div>
                    <div><span className="text-muted-foreground">Completed Value:</span> <span className="font-semibold text-emerald-500"><IndianRupee className="inline h-3.5 w-3.5" />{Number(perfData.tasks.completed_value).toLocaleString('en-IN')}</span></div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Ticket Resolution Stats</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-6 mb-4 text-sm">
                    <div><span className="text-muted-foreground">Resolved:</span> <span className="font-semibold">{perfData.tickets.resolved}/{perfData.tickets.total}</span></div>
                    <div><span className="text-muted-foreground">Avg Resolution Time:</span> <span className="font-semibold">{perfData.tickets.avg_resolution_hours} hrs</span></div>
                  </div>
                  {perfData.tickets.details.length > 0 && (
                    <Table>
                      <TableHeader><TableRow><TableHead>Ticket</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Resolution Time</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {perfData.tickets.details.map((tk, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-sm">{tk.ticket_id}</TableCell>
                            <TableCell>{tk.subject}</TableCell>
                            <TableCell><Badge variant="outline" className="capitalize">{tk.status.replace('_', ' ')}</Badge></TableCell>
                            <TableCell>{tk.resolution_hours != null ? `${tk.resolution_hours} hrs` : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {perfData.leads.total > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">SMB Leads Managed</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex gap-4 flex-wrap">
                      {Object.entries(perfData.leads.by_status).map(([status, count]) => (
                        <div key={status} className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">{status.replace('_', ' ')}</Badge>
                          <span className="font-semibold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="self-review" className="mt-4 space-y-3">
              {!empSelfReview ? (
                <Card><CardContent className="py-12 text-center">
                  <FileText className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No self-review submitted by this employee for {selectedFy}.</p>
                </CardContent></Card>
              ) : (
                <>
                  <div className="flex items-center gap-3 text-sm">
                    <Badge className={empSelfReview.status === 'submitted' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}>{empSelfReview.status}</Badge>
                    {empSelfReview.submitted_at && <span className="text-muted-foreground">Submitted on {formatDate(empSelfReview.submitted_at)}</span>}
                  </div>
                  {SELF_REVIEW_QUESTIONS.map(q => (
                    <Card key={q.key} data-testid={`admin-sr-${q.key}`}>
                      <CardContent className="p-3">
                        <p className="text-xs font-semibold text-primary mb-1">{q.label}</p>
                        <p className="text-sm whitespace-pre-wrap">{empSelfReview[q.key] || <span className="text-muted-foreground italic">No response</span>}</p>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </TabsContent>

            <TabsContent value="appraisal" className="space-y-4 mt-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Rating Parameters</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {RATING_PARAMS.map(p => (
                    <div key={p.key} className="flex items-center justify-between">
                      <Label className="text-sm">{p.label}</Label>
                      <RatingStars value={ratings[p.key]} onChange={v => setRatings({ ...ratings, [p.key]: v })} />
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <Label className="font-semibold">Average Rating</Label>
                    <span className="text-lg font-bold text-amber-400">{avgRating} / 5</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Qualitative Assessment</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2"><Label>Strengths</Label><Textarea value={strengths} onChange={e => setStrengths(e.target.value)} rows={3} placeholder="Key strengths demonstrated..." data-testid="appraisal-strengths" /></div>
                  <div className="space-y-2"><Label>Areas of Improvement</Label><Textarea value={improvements} onChange={e => setImprovements(e.target.value)} rows={3} placeholder="Areas where growth is needed..." data-testid="appraisal-improvements" /></div>
                  <div className="space-y-2"><Label>General Remarks</Label><Textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={3} placeholder="Overall remarks..." data-testid="appraisal-remarks" /></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Recommendation & CTC</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Recommended Action</Label>
                      <Select value={action} onValueChange={setAction}>
                        <SelectTrigger data-testid="appraisal-action"><SelectValue /></SelectTrigger>
                        <SelectContent>{ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Current CTC (INR)</Label><Input type="number" value={currentCtc} onChange={e => { setCurrentCtc(e.target.value); const cur = parseFloat(e.target.value); const pct = parseFloat(incrementPct); if (cur > 0 && pct) { setProposedCtc(String(Math.round(cur * (1 + pct / 100)))); } }} placeholder="Annual CTC" data-testid="appraisal-current-ctc" /></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Increment %</Label><Input type="number" step="0.01" value={incrementPct} onChange={e => { const pct = e.target.value; setIncrementPct(pct); const cur = parseFloat(currentCtc); if (cur > 0 && pct !== '') { setProposedCtc(String(Math.round(cur * (1 + parseFloat(pct) / 100)))); } }} placeholder="e.g. 15" data-testid="appraisal-increment-pct" /></div>
                    <div className="space-y-2"><Label>Proposed CTC (INR)</Label><Input type="number" value={proposedCtc} onChange={e => { const newCtc = e.target.value; setProposedCtc(newCtc); const cur = parseFloat(currentCtc); const proposed = parseFloat(newCtc); if (cur > 0 && proposed > 0) { setIncrementPct(String(Math.round((proposed - cur) / cur * 10000) / 100)); } else { setIncrementPct(''); } }} placeholder="Proposed CTC" data-testid="appraisal-proposed-ctc" /></div>
                  </div>
                  <Button onClick={handleSave} disabled={saving} data-testid="appraisal-save">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Appraisal
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Appraisal History</CardTitle></CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-4 text-center">No previous appraisals found.</p>
                  ) : (
                    <Table>
                      <TableHeader><TableRow><TableHead>FY</TableHead><TableHead>Avg Rating</TableHead><TableHead>Action</TableHead><TableHead>Current CTC</TableHead><TableHead>Proposed CTC</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {history.map(a => {
                          const r = a.ratings || {};
                          const vals = Object.values(r).filter(v => v > 0);
                          const avg = vals.length ? (vals.reduce((x, y) => x + y, 0) / vals.length).toFixed(1) : '-';
                          return (
                            <TableRow key={a.id}>
                              <TableCell className="font-medium">{a.fy}</TableCell>
                              <TableCell><span className="flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{avg}</span></TableCell>
                              <TableCell><Badge variant="outline">{a.recommended_action || '-'}</Badge></TableCell>
                              <TableCell>{a.current_ctc ? `${Number(a.current_ctc).toLocaleString('en-IN')}` : '-'}</TableCell>
                              <TableCell>{a.proposed_ctc ? `${Number(a.proposed_ctc).toLocaleString('en-IN')}` : '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{a.created_at?.split('T')[0] || '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

// =====================================================
// MAIN PAGE
// =====================================================
export const AppraisalPage = () => {
  const [employees, setEmployees] = useState([]);
  const [topTab, setTopTab] = useState('individual');
  const [reviewDialog, setReviewDialog] = useState({ open: false, employeeId: null, fy: null });

  useEffect(() => {
    api.get('/appraisals/employees').then(r => { if (r.data.success) setEmployees(r.data.data); }).catch(() => {});
  }, []);

  const openReviewDialog = (employeeId, fy) => setReviewDialog({ open: true, employeeId, fy });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="appraisal-page">
      <div>
        <h1 className="text-2xl font-bold">Employee Appraisals</h1>
        <p className="text-muted-foreground">Run appraisal cycles, collect self-reviews, and conduct 1:1 evaluations.</p>
      </div>

      <Tabs value={topTab} onValueChange={setTopTab}>
        <TabsList>
          <TabsTrigger value="individual" data-testid="top-tab-individual"><Users className="h-4 w-4 mr-1.5" />Individual Appraisal</TabsTrigger>
          <TabsTrigger value="cycles" data-testid="top-tab-cycles"><CalendarDays className="h-4 w-4 mr-1.5" />Appraisal Cycles</TabsTrigger>
        </TabsList>

        <TabsContent value="individual" className="mt-4">
          <IndividualAppraisal employees={employees} />
        </TabsContent>

        <TabsContent value="cycles" className="mt-4">
          <CycleManagement employees={employees} onViewSelfReview={openReviewDialog} />
        </TabsContent>
      </Tabs>

      {reviewDialog.open && (
        <ViewSelfReviewDialog
          employeeId={reviewDialog.employeeId}
          fy={reviewDialog.fy}
          open={reviewDialog.open}
          onClose={() => setReviewDialog({ open: false, employeeId: null, fy: null })}
        />
      )}
    </div>
  );
};
